import {
  Homeowner,
  HomeownerCheckResult,
  PropertyDetails,
  PropertyFeedbackResponse,
} from "../types/hom-property";
import { IHOMPropertyService } from "../types/services/hom-property-service";
import { DELETE, FetchResult, GET, PATCH, POST } from "../utils/fetch";

type HomeownersResponse = {
  home_owners: Homeowner[];
};

class HOMPropertyService implements IHOMPropertyService {
  async getHomeowners(slug: string): Promise<FetchResult<Homeowner[]>> {
    const url = `/api/v2/rental/properties/${slug}/home_owners`,
      { response, error } = await GET<HomeownersResponse>(url);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.home_owners, error: null };
  }

  async checkHomeownerPhone(
    mobile: string,
    country_code: string,
  ): Promise<FetchResult<HomeownerCheckResult>> {
    const qp = new URLSearchParams();
    qp.set("mobile", mobile);
    qp.set("country_code", country_code);

    const url = `/api/v2/rental/home_owners/check_phone?${qp.toString()}`,
      { response, error } = await GET<HomeownerCheckResult>(url);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response, error: null };
  }

  async addHomeownerToProperty(
    slug: string,
    homeowner_id: number,
  ): Promise<FetchResult<Homeowner>> {
    const url = `/api/v2/rental/properties/${slug}/home_owners`,
      { response, error } = await POST<{ home_owner: Homeowner }>(url, {
        homeowner_id,
      });

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.home_owner, error: null };
  }

  async createAndMapHomeowner(
    slug: string,
    payload: {
      name: string;
      email: string;
      mobile: string;
      country_code: string;
    },
  ): Promise<FetchResult<Homeowner>> {
    const url = `/api/v2/rental/properties/${slug}/home_owners`,
      { response, error } = await POST<{ home_owner: Homeowner }>(
        url,
        payload,
      );

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.home_owner, error: null };
  }

  async updateHomeowner(
    homeowner_id: number,
    payload: { name: string; email: string },
  ): Promise<FetchResult<Homeowner>> {
    const url = `/api/v2/rental/home_owners/${homeowner_id}`,
      { response, error } = await PATCH<{ home_owner: Homeowner }>(url, payload);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.home_owner, error: null };
  }

  async removeHomeowner(
    slug: string,
    homeowner_id: number,
  ): Promise<FetchResult<void>> {
    const url = `/api/v2/rental/properties/${slug}/home_owners/${homeowner_id}`,
      { error } = await DELETE<void>(url);

    if (error) {
      return { response: null, error };
    }

    return { response: null, error: null };
  }

  async getPropertyFeedbacks(
    slug: string,
    type: "guest" | "homeowner",
    page?: string,
  ): Promise<FetchResult<PropertyFeedbackResponse>> {
    const qp = new URLSearchParams();
    qp.set("type", type);
    qp.set("per_page", "5");
    if (page) {
      qp.set("page", page);
    }

    const url = `/api/v2/rental/properties/${slug}/feedbacks?${qp.toString()}`,
      { response, error } = await GET<PropertyFeedbackResponse>(url);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response, error: null };
  }

  async getPropertyDetails(
    slug: string,
  ): Promise<FetchResult<PropertyDetails>> {
    const url = `/api/v2/rental/properties/${slug}/property_details`,
      { response, error } = await GET<{ property_details: PropertyDetails }>(url);

    if (error) {
      return { response: null, error };
    }

    if (!response) {
      return { response: null, error: new Error("Unknown error") };
    }

    return { response: response.property_details, error: null };
  }
}

export default HOMPropertyService;
